export const ONE_DAY_IN_MS = 86400000;
export const ONE_MINUTE_IN_MS = 60000;
export const ONE_SECOND_IN_MS = 1000;

import dayjs from 'dayjs';
import { capitalize } from 'src/lib/utils/string.util';

/**
 * Returns a localized long day name string based on the provided date string and locale.
 *
 * @param dateString - The date string to format.
 * @param locale - The optional locale to use for formatting. Supported locales are 'es' (Spanish), 'en' (English), and 'eu' (Basque).
 * @returns The formatted long day name string.
 */
export const getLongDayNameLocalized = (dateString: string, locale?: string): string => {
  const date = dayjs(dateString);

  if (locale) {
    switch (locale) {
      case 'es':
        return `${capitalize(date.format('dddd, D'))} de ${capitalize(date.format('MMMM'))}`;
      case 'en':
        return `${capitalize(date.format('dddd, D'))} ${capitalize(date.format('MMMM'))}`;
      case 'eu':
        return `${capitalize(date.format('dddd'))}, ${capitalize(date.format('MMMM'))}k ${date.format('D')}`;
    }
  }

  return `${capitalize(date.format('dddd, D'))} ${capitalize(date.format('MMMM'))}`;
};

/**
 * Calculates the minutes and seconds from a given number of seconds.
 *
 * @param seconds - The total number of seconds to be converted. If not provided, defaults to 0.
 * @returns An object containing the calculated minutes and remaining seconds.
 */
export const calculateMinutesAndSecondsFromSeconds = (seconds?: number) => {
  if (!seconds) return { minutes: 0, seconds: 0 };
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return { minutes, seconds: remainingSeconds };
};

/**
 * Generates an array of select options for notification hours.
 * Each option contains a value (hour in 24-hour format) and a label (formatted as "HH:00").
 *
 * @returns {Array<{ value: number; label: string }>} An array of objects representing the select options.
 */
export const generateNotificationHoursSelectOptions = () => {
  const options: { value: number; label: string }[] = [];
  Array.from({ length: 24 }, (_, i) => i).forEach((value) => {
    const label = Number(value) < 10 ? `0${value}:00` : `${value}:00`;
    options.push({ value: Number(value), label });
  });

  return options;
};

/**
 * Get the current month to ISO format
 * @returns Date - The current month
 */
export const getCurrentMonthToISO = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString();
};

/**
 * HH:MM - day Month
 */
export const getFormattedDateLocalized = (date: string, locale: string) => {
  const month = new Date(date).toLocaleDateString(locale, { month: 'long' });
  const day = new Date(date).toLocaleDateString(locale, { day: 'numeric' });
  const year = new Date(date).toLocaleDateString(locale, { year: 'numeric' });
  const hour = new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  return `${hour} - ${day} ${month} ${year}`;
};

export const formatTimeMMSS = (miliseconds: number) => {
  if (isNaN(miliseconds) || miliseconds === null) {
    return '0:00';
  }

  const totalSeconds = Math.floor(miliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

  return `${minutes}:${formattedSeconds}`;
};

export const millisToSeconds = (ms: number) => {
  return Math.floor(ms / ONE_SECOND_IN_MS);
};

/**
 * Get the first day of the month in UTC
 * @returns Date - The first day of the month in UTC
 */
export const getFirstDayOfMonthUTC = () => {
  const date = new Date();
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  return new Date(Date.UTC(year, month, 1));
};

/**
 * Calculate the time difference between the target date and now
 * @param targetDate - The target date
 */
export const calculateTimeDifference = (targetDate: Date) => {
  const now = new Date();
  const timeDifferenceMs = targetDate.getTime() - now.getTime();

  const daysLeft = Math.floor(timeDifferenceMs / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.floor((timeDifferenceMs / (1000 * 60 * 60)) % 24);
  const minutesLeft = Math.floor((timeDifferenceMs / (1000 * 60)) % 60);
  const secondsLeft = Math.floor((timeDifferenceMs / 1000) % 60);

  return { daysLeft, hoursLeft, minutesLeft, secondsLeft };
};

/**
 * Returns a promise that resolves after a specified delay.
 * This function can be used to introduce a delay in asynchronous operations.
 *
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Promise<void>} A promise that resolves after the specified delay.
 *
 * @example
 * // Usage example:
 * delay(1000).then(() => {
 *   console.log('Executed after 1 second');
 * });
 */
export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get the month name from a month number
 * @param monthNumber - The month number (1-12)
 * @returns string - The name of the month
 */
export function getMonthName(monthNumber: number): string {
  return dayjs()
    .month(monthNumber - 1)
    .format('MMMM');
}

/**
 * Get the user timezone
 * @returns {string} The user timezone (e.g. 'Europe/Madrid')
 */
export const getUserTimezone = (): string => {
  if (typeof window !== 'undefined') {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error('Error getting user timezone:', error);
      return 'UTC';
    }
  }
  return 'UTC';
};
